import { Link as ScrollLink } from 'react-scroll';
import { SiteContent } from '../content';

type HeroContent = SiteContent['hero'];

interface HeroProps {
  content: HeroContent;
  getImageUrl: (name: string) => string;
}

export default function Hero({ content, getImageUrl }: HeroProps) {
  const backgroundImage = content.backgroundImage
    ? getImageUrl(content.backgroundImage)
    : null;

  return (
    <section
      id="hero"
      className="relative w-full flex items-center justify-center overflow-hidden hero-fullscreen"
    >
      {/* Background Image or Placeholder */}
      {backgroundImage ? (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-milan-gray-dark to-milan-black" />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white font-semibold mb-4 animate-fade-in-up">
          {content.title}
        </h1>
        <p className="text-xl sm:text-2xl md:text-3xl text-white/90 font-light mb-6 animate-fade-in-up animate-delay-100">
          {content.subtitle}
        </p>
        <p className="text-lg sm:text-xl text-white font-medium max-w-2xl mx-auto mb-6 leading-relaxed animate-fade-in-up animate-delay-200 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          {content.description}
        </p>
        {content.announcement && (
          <p className="inline-block border-4 border-white bg-black/40 px-6 py-4 sm:px-10 sm:py-5 text-lg sm:text-xl md:text-[1.75rem] md:leading-snug text-white font-bold uppercase tracking-wider [text-wrap:balance] mb-10 animate-fade-in-up animate-delay-200 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            {content.announcement}
          </p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animate-delay-300">
          <ScrollLink
            to="rooms"
            smooth={true}
            duration={800}
            offset={-80}
            className="btn-primary cursor-pointer"
          >
            Explore the Property
          </ScrollLink>
          <ScrollLink
            to="contact"
            smooth={true}
            duration={800}
            offset={-80}
            className="btn-outline cursor-pointer"
          >
            Book Your Stay
          </ScrollLink>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ScrollLink
          to="rooms"
          smooth={true}
          duration={800}
          offset={-80}
          className="cursor-pointer"
        >
          <svg
            className="w-8 h-8 text-white/70"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </ScrollLink>
      </div>
    </section>
  );
}
